/**
 * Dimensional analysis over a parsed expression (plan §5.2).
 *
 * The one subtlety worth stating: a **numeric literal is polymorphic**. `max(1, valuationYear -
 * modelYear + 1)` is the seed policy's very first equation, and the `1` in it is a year; the `1` in
 * `base * (1 - dep)` is a fraction. Rather than ask an admin to annotate literals, an unadorned number
 * unifies with whatever it is combined with, and only *named* quantities carry a dimension the checker
 * enforces. That is enough to reject `inspected + odometer` — the two names disagree — while accepting
 * every formula in the product plan unchanged.
 */
import { EngineError, expressionError, throwEngineError } from '../errors.js';
import { COMPARISON_OPERATORS } from '../expressions/parser.js';
import { DIMENSIONLESS, dimensionsEqual, divideDimensions, formatDimension, isDimensionless, multiplyDimensions, powerDimension, } from './dimension.js';
import { dimensionForUnit, unitNameForDimension } from './units.js';
export const LITERAL = { kind: 'literal' };
export const dimensionValue = (dimension) => ({ kind: 'dimension', dimension });
export const dimensionOfValue = (value) => (value.kind === 'literal' ? DIMENSIONLESS : value.dimension);
export const describeValue = (value) => (value.kind === 'literal' ? 'a number' : formatDimension(value.dimension));
const unify = (a, b, node, context) => {
    if (a.kind === 'literal') {
        return b;
    }
    if (b.kind === 'literal') {
        return a;
    }
    if (!dimensionsEqual(a.dimension, b.dimension)) {
        return throwEngineError('UNIT_MISMATCH', `${context} mixes ${formatDimension(a.dimension)} with ${formatDimension(b.dimension)}`, {
            position: node.start,
        });
    }
    return a;
};
const literalValueOf = (node) => {
    if (node.type === 'number') {
        return Number(node.value);
    }
    if (node.type === 'unary' && node.operand.type === 'number') {
        return node.op === '-' ? -Number(node.operand.value) : Number(node.operand.value);
    }
    return null;
};
/** Infers the unit of a node, throwing `UNIT_MISMATCH` at the first inconsistency. */
export const inferUnit = (node, resolve) => {
    switch (node.type) {
        case 'number':
            return LITERAL;
        case 'variable': {
            const unit = resolve(node.name);
            if (unit === null) {
                // Unknown references are reported by the compiler with their own code; treat them as literals
                // here so one missing name does not also produce a confusing unit complaint.
                return LITERAL;
            }
            return dimensionValue(dimensionForUnit(unit));
        }
        case 'unary':
            return inferUnit(node.operand, resolve);
        case 'binary': {
            const left = inferUnit(node.left, resolve);
            const right = inferUnit(node.right, resolve);
            if (node.op === '+' || node.op === '-') {
                return unify(left, right, node, `"${node.op}"`);
            }
            if (COMPARISON_OPERATORS.includes(node.op)) {
                unify(left, right, node, `the comparison "${node.op}"`);
                return dimensionValue(DIMENSIONLESS);
            }
            if (node.op === '*') {
                return left.kind === 'literal' && right.kind === 'literal'
                    ? LITERAL
                    : dimensionValue(multiplyDimensions(dimensionOfValue(left), dimensionOfValue(right)));
            }
            if (node.op === '/') {
                return left.kind === 'literal' && right.kind === 'literal'
                    ? LITERAL
                    : dimensionValue(divideDimensions(dimensionOfValue(left), dimensionOfValue(right)));
            }
            // `^`
            if (right.kind === 'dimension' && !isDimensionless(right.dimension)) {
                return throwEngineError('UNIT_MISMATCH', `An exponent must be a plain number, not ${formatDimension(right.dimension)}`, {
                    position: node.right.start,
                });
            }
            if (left.kind === 'literal') {
                return LITERAL;
            }
            if (isDimensionless(left.dimension)) {
                return dimensionValue(DIMENSIONLESS);
            }
            const exponent = literalValueOf(node.right);
            if (exponent === null || !Number.isInteger(exponent)) {
                return throwEngineError('UNIT_MISMATCH', `${formatDimension(left.dimension)} can only be raised to a whole-number power written as a literal`, { position: node.right.start });
            }
            return dimensionValue(powerDimension(left.dimension, exponent));
        }
        case 'call': {
            if (node.name === 'if') {
                const condition = inferUnit(node.args[0], resolve);
                if (condition.kind === 'dimension' && !isDimensionless(condition.dimension)) {
                    return throwEngineError('UNIT_MISMATCH', `An if() condition must be a comparison or a flag, not ${formatDimension(condition.dimension)}`, {
                        position: node.args[0].start,
                    });
                }
                return unify(inferUnit(node.args[1], resolve), inferUnit(node.args[2], resolve), node, 'the two branches of if()');
            }
            if (node.name === 'min' || node.name === 'max') {
                return node.args
                    .map((arg) => inferUnit(arg, resolve))
                    .reduce((accumulator, value) => unify(accumulator, value, node, `${node.name}()`), LITERAL);
            }
            // round / ceil / floor / abs keep whatever they were given.
            return inferUnit(node.args[0], resolve);
        }
        default:
            return LITERAL;
    }
};
/**
 * Checks a whole equation body against its declared unit. A body made only of literals (`commission =
 * 300`) satisfies any declaration — the number *is* whatever the admin says it is.
 */
export const checkDeclaredUnit = (node, declared, resolve, equationId) => {
    let inferred;
    try {
        inferred = inferUnit(node, resolve);
    }
    catch (error) {
        if (error instanceof EngineError) {
            throw new EngineError({ ...error.error, equationId });
        }
        throw error;
    }
    if (inferred.kind === 'literal') {
        return;
    }
    const expected = dimensionForUnit(declared);
    if (!dimensionsEqual(inferred.dimension, expected)) {
        const actual = unitNameForDimension(inferred.dimension) ?? formatDimension(inferred.dimension);
        throw new EngineError(expressionError('UNIT_MISMATCH', `"${equationId}" is declared ${declared} but its expression works out to ${actual}`, {
            equationId,
            token: declared,
        }));
    }
};
//# sourceMappingURL=infer.js.map